/* eslint-disable no-underscore-dangle */
import { VueConstructor } from 'vue';
import { DirectiveBinding } from 'vue/types/options';
import { VNode } from 'vue/types/umd';

interface TouchBindingParameters{
  left: (event: TouchEventWrapper)=>void;
  right: (event: TouchEventWrapper)=>void;
  up: (event: TouchEventWrapper)=>void;
  down: (event: TouchEventWrapper)=>void;
  start: (event: TouchEventWrapper)=>void;
  move: (event: TouchEventWrapper)=>void;
  end: (event: TouchEventWrapper)=>void;
}

class TouchEventWrapper implements TouchBindingParameters {
  touchstartX: number = 0;
  touchstartY: number = 0;
  touchendX: number = 0;
  touchendY: number = 0;
  touchmoveX: number = 0;
  touchmoveY: number = 0;
  offsetX: number = 0;
  offsetY: number = 0;

  left: (event: TouchEventWrapper)=>void = () => {};
  right: (event: TouchEventWrapper)=>void = () => {};
  up: (event: TouchEventWrapper)=>void = () => {};
  down: (event: TouchEventWrapper)=>void = () => {};
  start: (event: TouchEventWrapper)=>void = () => {};
  move: (event: TouchEventWrapper)=>void = () => {};
  end: (event: TouchEventWrapper)=>void = () => {};
}

export default {
  install: (Vue: VueConstructor, options: any) => {
    const handleGesture = (wrapper: TouchEventWrapper) => {
      const {
        touchstartX, touchendX, touchstartY, touchendY,
      } = wrapper;
      const dirRatio = 0.5;
      const minDistance = 16;
      wrapper.offsetX = touchendX - touchstartX;
      wrapper.offsetY = touchendY - touchstartY;

      if (Math.abs(wrapper.offsetY) < dirRatio * Math.abs(wrapper.offsetX)) {
        if (wrapper.left !== undefined && (touchendX < touchstartX - minDistance)) {
          wrapper.left(wrapper);
        }
        if (wrapper.right !== undefined && (touchendX > touchstartX + minDistance)) {
          wrapper.right(wrapper);
        }
      }

      if (Math.abs(wrapper.offsetX) < dirRatio * Math.abs(wrapper.offsetY)) {
        if (wrapper.up !== undefined && (touchendY < touchstartY - minDistance)) {
          wrapper.up(wrapper);
        }
        if (wrapper.down !== undefined && (touchendY > touchstartY + minDistance)) {
          wrapper.down(wrapper);
        }
      }
    };

    function touchstart(event: TouchEvent, wrapper: TouchEventWrapper) {
      const touch = event.changedTouches[0];
      wrapper.touchstartX = touch.clientX;
      wrapper.touchstartY = touch.clientY;

      if (wrapper.start !== undefined) {
        wrapper.start(Object.assign(event, wrapper));
      }
    }

    function touchend(event: TouchEvent, wrapper: TouchEventWrapper) {
      const touch = event.changedTouches[0];
      wrapper.touchendX = touch.clientX;
      wrapper.touchendY = touch.clientY;

      if (wrapper.end !== undefined) {
        wrapper.end(Object.assign(event, wrapper));
      }

      handleGesture(wrapper);
    }

    function touchmove(event: TouchEvent, wrapper: TouchEventWrapper) {
      const touch = event.changedTouches[0];
      wrapper.touchmoveX = touch.clientX;
      wrapper.touchmoveY = touch.clientY;

      if (wrapper.move !== undefined) {
        wrapper.move(Object.assign(event, wrapper));
      }
    }

    function createHandlers(value: TouchBindingParameters) {
      const wrapper = new TouchEventWrapper();
      wrapper.left = value.left;
      wrapper.right = value.right;
      wrapper.up = value.up;
      wrapper.down = value.down;
      wrapper.start = value.start;
      wrapper.move = value.move;
      wrapper.end = value.end;

      return {
        touchstart: (e: TouchEvent) => touchstart(e, wrapper),
        touchend: (e: TouchEvent) => touchend(e, wrapper),
        touchmove: (e: TouchEvent) => touchmove(e, wrapper),
      };
    }

    function inserted(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
      const { value } = binding;
      const target = value.parent ? el.parentElement : el;

      // Needed to pass unit tests
      if (!target) return;

      const handlers = createHandlers(binding.value);
      (target as any).$touchHandlers = Object((target as any).$touchHandlers);
      (target as any).$touchHandlers[(vnode.context as any)._uid] = handlers;

      Object.keys(handlers).forEach((eventName) => {
        target.addEventListener(eventName, (handlers as any)[eventName], { passive: true });
      });
    }

    function unbind(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
      const target = (binding.value.parent ? el.parentElement : el) as any;
      const touchHandlers = target.$touchHandlers;
      if (!target || !touchHandlers) return;

      const handlers = (touchHandlers as any)[(vnode.context as any)._uid];
      Object.keys(handlers).forEach((eventName) => {
        target.removeEventListener(eventName, handlers[eventName]);
      });
      delete target.$touchHandlers[(vnode.context as any)._uid];
    }

    Vue.directive('touch', { inserted, unbind });
  },
};
